// Sidebar configuration for all departments

export interface Link {
  label: string;
  href: string;
  icon: string;
  isExternal?: boolean;
  isPlaceholder?: boolean;
  pageId?: string; // ID from pages.config.ts for permission checking
  hideWithoutAccess?: boolean; // Hide (instead of disabling) when the user lacks access to pageId
  isNew?: boolean; // Show "NEW" badge to highlight recently added pages
}

/**
 * True when a link opts into `hideWithoutAccess` and the user lacks access to its page.
 * External links and links without a pageId are never hidden.
 */
export function isHiddenWithoutAccess(link: Link, accessiblePages: Set<string>): boolean {
  return !!link.hideWithoutAccess && !link.isExternal && !!link.pageId && !accessiblePages.has(link.pageId);
}

export interface SidebarConfig {
  name: string; // Short display name (e.g., "Parts")
  title: string; // Full title (e.g., "BOTR Parts Tools")
  icon: string;
  color: string;
  links: Link[];
  externalLinks?: Link[];
}

export type Department = 'parts' | 'rental' | 'supplies';

export const sidebarConfigs: Record<Department, SidebarConfig> = {
  parts: {
    name: 'Parts',
    title: 'BOTR Parts Tools',
    icon: 'fa-toolbox',
    color: 'cyan',
    links: [
      { label: 'Part Label Printer', href: '/parts/label-printer', icon: 'fa-print', pageId: 'label-printer' },
      { label: 'Backorders', href: '/parts/backorders', icon: 'fa-boxes-stacked', pageId: 'backorders' },
      { label: 'Vendor Logins', href: '/parts/vendors', icon: 'fa-right-to-bracket', pageId: 'vendors' },
      { label: 'BOL Generator', href: '/parts/bill-of-lading', icon: 'fa-file-invoice', pageId: 'billOfLading' },
      { label: 'Phone Directory', href: '/parts/directory', icon: 'fa-phone', pageId: 'directory' },
      { label: 'Quizzes', href: '/parts/quizzes', icon: 'fa-clipboard-question', pageId: 'quizzes', hideWithoutAccess: true },
      { label: 'Parts References', href: '/parts/references', icon: 'fa-book', pageId: 'bobcatBook' },
      { label: 'Bolt Decoder', href: '/parts/bolt-decoder', icon: 'fa-bolt', pageId: 'bolt-decoder' },
      { label: 'Tracks X-Ref', href: '/parts/tracks', icon: 'fa-wrench', pageId: 'tracks' },
    ],
    externalLinks: [
      { label: 'Bobcat Dealer Login', href: 'http://dealer.bobcat.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ProWork', href: 'https://secure3.saashr.com/ta/BerryCompanies.login', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'BerryConnect', href: 'https://connect.berrycompaniesinc.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'IT MOJO Ticket', href: 'https://berryco.mojohelpdesk.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ERIC Dashboard', href: 'https://app.powerbi.com/view?r=eyJrIjoiOTBiYTQ5YzgtZTk1MC00MjdkLThjMjYtNDgzODFhNjQ3ZWFjIiwidCI6IjYxZjRmYmY3LWJhMjItNGFhZi1hNjIzLTRlNTdhNmM0ZjBiMyJ9&pageName=27f180dfbcde33814458', icon: 'fa-arrow-up-right-from-square', isExternal: true },
    ],
  },
  rental: {
    name: 'Rental',
    title: 'BOTR Rental Tools',
    icon: 'fa-truck-ramp-box',
    color: 'orange',
    links: [
      { label: 'Rental Board', href: '/rental/rentals', icon: 'fa-clipboard-list', pageId: 'rentals' },
      { label: 'Equipment Search', href: '/rental/equipment-search', icon: 'fa-magnifying-glass', pageId: 'equipmentSearch' },
      { label: 'Yard Ops', href: '/rental/yard', icon: 'fa-warehouse', pageId: 'yard' },
      { label: 'Trucking Calendar', href: '/rental/trucking-calendar', icon: 'fa-calendar-week', pageId: 'truckingCalendar' },
      { label: 'Trucking Companies', href: '/rental/trucking-companies', icon: 'fa-truck', pageId: 'truckingCompanies' },
      { label: 'Photo Archive', href: '/rental/archive', icon: 'fa-camera-retro', pageId: 'archive' },
      { label: 'Lost Revenue', href: 'https://berrycompanies-my.sharepoint.com/:x:/g/personal/dheck_bobcatoftherockies_com/IQBKdclheyggSZKMxc97a-grAYqfqrZDjsJPZM8FVV7rsDg?email=JHall%40bobcatoftherockies.com&e=P67Nsv', icon: 'fa-arrow-trend-down', isExternal: true },
      { label: 'Fuel Calculator', href: '/rental/fuel-calculator', icon: 'fa-gas-pump', pageId: 'fuel-calculator' },
      { label: 'Rental References', href: '/rental/references', icon: 'fa-book', pageId: 'rentalReferences' },
      { label: 'Phone Directory', href: '/rental/directory', icon: 'fa-phone', pageId: 'directory' },
      { label: 'Quizzes', href: '/rental/quizzes', icon: 'fa-clipboard-question', pageId: 'quizzes', hideWithoutAccess: true },
    ],
    externalLinks: [
      { label: 'Bobcat Dealer Login', href: 'http://dealer.bobcat.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ProWork', href: 'https://secure3.saashr.com/ta/BerryCompanies.login', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'BerryConnect', href: 'https://connect.berrycompaniesinc.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'IT MOJO Ticket', href: 'https://berryco.mojohelpdesk.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ERIC Dashboard', href: 'https://app.powerbi.com/view?r=eyJrIjoiOTBiYTQ5YzgtZTk1MC00MjdkLThjMjYtNDgzODFhNjQ3ZWFjIiwidCI6IjYxZjRmYmY3LWJhMjItNGFhZi1hNjIzLTRlNTdhNmM0ZjBiMyJ9&pageName=27f180dfbcde33814458', icon: 'fa-arrow-up-right-from-square', isExternal: true },
    ],
  },
  supplies: {
    name: 'Supplies',
    title: 'BOTR Supplies Tools',
    icon: 'fa-boxes-stacked',
    color: 'teal',
    links: [
      { label: 'Stamp Board', href: '/supplies/stamps', icon: 'fa-stamp', pageId: 'stamps' },
      { label: 'Supply References', href: '/supplies/references', icon: 'fa-book', pageId: 'suppliesBook' },
      { label: 'Phone Directory', href: '/supplies/directory', icon: 'fa-phone', pageId: 'directory' },
      { label: 'Quizzes', href: '/supplies/quizzes', icon: 'fa-clipboard-question', pageId: 'quizzes', hideWithoutAccess: true },
    ],
    externalLinks: [
      { label: 'Bobcat Dealer Login', href: 'http://dealer.bobcat.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ProWork', href: 'https://secure3.saashr.com/ta/BerryCompanies.login', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'BerryConnect', href: 'https://connect.berrycompaniesinc.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'IT MOJO Ticket', href: 'https://berryco.mojohelpdesk.com/', icon: 'fa-arrow-up-right-from-square', isExternal: true },
      { label: 'ERIC Dashboard', href: 'https://app.powerbi.com/view?r=eyJrIjoiOTBiYTQ5YzgtZTk1MC00MjdkLThjMjYtNDgzODFhNjQ3ZWFjIiwidCI6IjYxZjRmYmY3LWJhMjItNGFhZi1hNjIzLTRlNTdhNmM0ZjBiMyJ9&pageName=27f180dfbcde33814458', icon: 'fa-arrow-up-right-from-square', isExternal: true },
    ],
  },
};

// Canonical order of departments (used by IndexPage, FavoritesSection, dropdown, etc.)
export const departmentOrder: Department[] = ['parts', 'rental', 'supplies'];

/**
 * Derive department from a URL pathname.
 * Returns the department if the first path segment matches a known department key, else null.
 */
export function getDepartmentFromPath(pathname: string): Department | null {
  const first = pathname.split('/').filter(Boolean)[0];
  return first && first in sidebarConfigs ? (first as Department) : null;
}

/**
 * Department for a given accent hue.
 *
 * Multi-department UI (index cards, the department dropdown, admin panels)
 * receives an accent hue rather than a department id, but the accent tokens are
 * keyed by department. This is the 1:1 bridge — it replaced a 5x3 table of raw
 * Tailwind classes; colors themselves now live in the `--color-accent-*` tokens
 * and are selected by a `data-department` attribute. See docs/STYLING.md.
 */
export const departmentByColor: Record<string, Department> = {
  cyan: 'parts',
  orange: 'rental',
  teal: 'supplies',
};
